import { ApiErrorCode } from '@orn/global/errors/apiErrorCodes.js';
import { validatePhoneNumber } from '@orn/shared-web/components/Input/PhoneNumberInput';
import validateEmail from '@orn/shared-web/lib/validation/emailValidation';

import { HousingOwnership, ResidenceType } from '../../../__generated__/globalTypes';
import { parseIsoToDateOfBirth, toIsoDate } from '../../../lib/dependents/helpers';
import { isCompleteAddress } from '../../../lib/dependents/residenceAddress';
import { extractErrorCode } from '../../../utils/apollo/error';

import {
  buildResidenceInputs,
  hydrateFormState,
  isResidenceCascadeComplete,
  type IFrequentPlace,
  type IResidenceFormState,
} from './residenceForm';

import type {
  CaregiverRelationshipKind,
  UpdateCaregiverRelationshipInput,
  UpdateDependantInput,
  UpdateDependentEmailInput,
  UpdateDependentPhoneNumberInput,
  UpsertResidenceInput,
} from '../../../__generated__/globalTypes';
import type { IDependentResidences } from '../../../hooks/queries/useDependentResidences';
import type { IDependent } from '../../../hooks/queries/useDependents';
import type { IDateOfBirthValue } from '@orn/shared-web/components/DateOfBirthInput';
import type { GraphQLFormattedError } from 'graphql';

/**
 * Which residence editor a card renders. `referential` = the second dependent
 * of a cohabiting pair — they live with the other dependent, whose card owns the
 * residence, so a referential card emits no residence of its own.
 */
export type ResidenceMode = 'main' | 'referential';

/** Editable per-dependent card state. The residence slice is the co-located `IResidenceFormState` sub-form. */
export interface IDependentCardState {
  firstName: string;
  lastName: string;
  preferredName: string;
  relationship: CaregiverRelationshipKind | null;
  isStep: boolean;
  isInLaw: boolean;
  dateOfBirth: IDateOfBirthValue | null;
  email: string;
  phone: string;
  residence: IResidenceFormState;
}

/**
 * Seed editable state from a persisted dependent + their reachable residences.
 * The residence slice goes through `hydrateFormState`, which only adopts a
 * primary the dependent actually owns (never a partner's or the caregiver's
 * resolved primary — that would reparent another member's row on save).
 */
export const seedCardState = (dep: IDependent, residences: IDependentResidences | null): IDependentCardState => {
  // A checkbox can't represent "unanswered" — coerce a null lives-with-caregiver
  // to `false` BEFORE hydrating so an owned residence actually hydrates (the
  // shared `hydrateFormState` only adopts a primary when the answer is an
  // explicit `false`) and a picked address saves.
  const residence = hydrateFormState(dep.id, dep.livesWithCaregiver ?? false, residences);
  return {
    firstName: dep.firstName,
    lastName: dep.lastName,
    preferredName: dep.preferredName ?? '',
    relationship: dep.relationship,
    isStep: dep.isStep,
    isInLaw: dep.isInLaw,
    dateOfBirth: parseIsoToDateOfBirth(dep.dateOfBirth),
    email: dep.email ?? '',
    phone: dep.phoneNumber ?? '',
    residence,
  };
};

/** The mutation inputs a single card needs at save time — only the changed slices are present. Residence deletes are computed once at the form level. */
export interface ICardInputs {
  dependent?: UpdateDependantInput;
  relationship?: UpdateCaregiverRelationshipInput;
  /** A cleared number for a member nobody has enrolled — the one contact write only the profile takes. */
  contact?: UpdateDependantInput;
  /**
   * A changed or cleared email address, whichever row holds it. Its own write,
   * apart from `contact`, so a refusal lands under the field it names.
   */
  email?: UpdateDependentEmailInput;
  /**
   * A changed number, whichever row holds it. One write for both linkage paths
   * because it is the only one that retires a handshake still out on the old
   * number — see the routing in {@link buildCardInputs}.
   */
  phone?: UpdateDependentPhoneNumberInput;
  residence?: UpsertResidenceInput;
}

/**
 * Whether the typed number differs from the one on file, compared canonically.
 *
 * Punctuation is not a change: `(206) 555-0114` and `+12065550114` are one
 * number, and a raw string compare would fire a mutation writing the same value
 * — which clears `phone_number_verified_at` and closes the text channel over a
 * reformat. Unparseable input falls back to the trimmed text, so a half-typed
 * number still reads as changed.
 *
 * Exported because two things have to agree on it: the mutation this builds,
 * and the card's warning that saving costs the existing verification. Two
 * derivations of "changed" would eventually disagree, and the visible half is
 * the one that would be wrong.
 */
export function hasPhoneChanged(state: IDependentCardState, dep: IDependent): boolean {
  const canonical = (value: string): string => validatePhoneNumber(value, false, true).e164Value ?? value.trim();
  return canonical(state.phone) !== canonical(dep.phoneNumber ?? '');
}

/**
 * Whether the email on screen differs from the one saved on the row.
 *
 * Compared case-insensitively on the trimmed text, so re-typing the same address
 * in different case is not a change. Clearing a saved address counts as one.
 *
 * Exported for the same reason as {@link hasPhoneChanged}: the gate below and any
 * warning about an unsaved address have to agree on what "changed" means.
 */
export function hasEmailChanged(state: IDependentCardState, dep: IDependent): boolean {
  const canonical = (value: string): string => value.trim().toLowerCase();
  return canonical(state.email) !== canonical(dep.email ?? '');
}

/**
 * The error under a card's email field, or `undefined` when the address is fine.
 *
 * A blank address is fine until someone enrolls. After that it is an error, not a
 * clear: the account is reached on the address, and the server refuses to remove
 * it. {@link isCardValid} reads this too, so the gate and the message cannot
 * disagree about what blocks a save.
 */
export function emailFieldError(email: string, isAccountBacked: boolean): string | undefined {
  if (!email.trim()) {
    return isAccountBacked ? 'Email address is required' : undefined;
  }
  const { isValid, error } = validateEmail(email);
  return isValid ? undefined : (error ?? 'Please enter a valid email address');
}

/**
 * Whether a loved one's number is held back waiting on their email address.
 *
 * Not a rule about phone numbers — it is the account. Proving a number opens a
 * group text whose acceptance mints an account, and `users.email` is `NOT NULL`,
 * so the server reads the **stored** address and refuses without one.
 *
 * So the address has to be **saved, valid, and the one on screen** — all three.
 * A valid address the caregiver has typed but not flushed is missing when the
 * server looks; a saved address the caregiver has since cleared or replaced is
 * the wrong one, and proving the number would mint the account under an address
 * the screen no longer shows. Both are silent, which is why the gate holds until
 * the field settles rather than letting the write and the send race.
 *
 * This is the rule `verifyState` already applies to the number itself, where an
 * unsaved edit hides the button because verifying would prove the stored value
 * rather than the visible one. Same reasoning, one field over.
 */
export function needsEmailBeforePhone({
  isAccountBacked,
  hasEmailOnFile,
  hasEmailError,
  isEmailChanged,
}: {
  isAccountBacked: boolean;
  hasEmailOnFile: boolean;
  hasEmailError: boolean;
  isEmailChanged: boolean;
}): boolean {
  return !isAccountBacked && (!hasEmailOnFile || hasEmailError || isEmailChanged);
}

const graphQLErrorsOf = (reason: unknown): readonly GraphQLFormattedError[] =>
  typeof reason === 'object' && reason !== null && 'graphQLErrors' in reason && Array.isArray(reason.graphQLErrors)
    ? (reason.graphQLErrors as readonly GraphQLFormattedError[])
    : [];

/**
 * What to put under one card's phone field when the server refused its number.
 *
 * The form's own banner cannot carry this. A household can hold several loved
 * ones, every card saves through the same commit, and "already verified by
 * another account" names one number — read at the top of the page it does not
 * say whose. `isCardValid` has already rejected anything unparseable, so the
 * refusal that actually reaches here is the collision.
 */
export function phoneRefusalMessage(reason: unknown): string {
  return extractErrorCode(graphQLErrorsOf(reason)) === ApiErrorCode.SMS_PHONE_ALREADY_VERIFIED
    ? 'Another account has already verified this number.'
    : "Couldn't save this number. Please try again.";
}

/**
 * What to put under one card's email field when the server refused its address.
 *
 * Kept off the banner for the reason {@link phoneRefusalMessage} gives.
 * `emailFieldError` has already held back a malformed address and an enrolled
 * loved one's blank one, which leaves two refusals a caregiver can act on.
 */
export function emailRefusalMessage(reason: unknown): string {
  switch (extractErrorCode(graphQLErrorsOf(reason))) {
    case ApiErrorCode.AUTH_EMAIL_ALREADY_REGISTERED:
      return 'Another account already uses this email address.';
    // Clearing the address while a phone verification is out, whose acceptance
    // sets up the account under it.
    case ApiErrorCode.DEPENDENT_VERIFICATION_ALREADY_RUNNING:
      return 'A verification is underway and needs this address. Change it instead of clearing it.';
    default:
      return "Couldn't save this email address. Please try again.";
  }
}

/**
 * Diff one card's edited state against its saved baseline and produce only the
 * mutation inputs that changed. Identity (name + dob) maps to `updateDependant`;
 * the email and the phone each map to their own write; relationship
 * kind/step/in-law AND the lives-with-you answer map to
 * `updateCaregiverRelationship`; a changed Main-mode living situation maps to an
 * `upsertResidence`. Referential cards never emit a residence (they live with
 * the other dependent, whose card owns it). Returns `{}` when nothing changed.
 */
export const buildCardInputs = (
  dep: IDependent,
  mode: ResidenceMode,
  state: IDependentCardState,
  baseline: IDependentCardState
): ICardInputs => {
  const inputs: ICardInputs = {};

  const identityChanged =
    state.firstName.trim() !== baseline.firstName.trim() ||
    state.lastName.trim() !== baseline.lastName.trim() ||
    state.preferredName.trim() !== baseline.preferredName.trim() ||
    toIsoDate(state.dateOfBirth) !== toIsoDate(baseline.dateOfBirth);
  if (identityChanged) {
    inputs.dependent = {
      dependantId: dep.id,
      firstName: state.firstName.trim(),
      lastName: state.lastName.trim(),
      preferredName: state.preferredName.trim() || null,
      // The `CalendarDate` scalar maps to a class type, but the wire accepts the
      // ISO string `toIsoDate` produces — cast to satisfy the generated input type.
      dateOfBirth: toIsoDate(state.dateOfBirth) as unknown as UpdateDependantInput['dateOfBirth'],
    };
  }

  const relationshipChanged =
    state.relationship !== baseline.relationship ||
    state.isStep !== baseline.isStep ||
    state.isInLaw !== baseline.isInLaw;
  const livesWithChanged = state.residence.liveWithCaregiver !== baseline.residence.liveWithCaregiver;
  if ((relationshipChanged && state.relationship) || livesWithChanged) {
    const relationship: UpdateCaregiverRelationshipInput = { caregiverRelationshipId: dep.caregiverRelationshipId };
    if (relationshipChanged && state.relationship) {
      relationship.relationship = state.relationship;
      relationship.isStep = state.isStep;
      relationship.isInLaw = state.isInLaw;
    }
    if (livesWithChanged && state.residence.liveWithCaregiver !== null) {
      relationship.livesWithCaregiver = state.residence.liveWithCaregiver;
    }
    inputs.relationship = relationship;
  }

  // A changed number goes through `updateDependentPhoneNumber` whichever row
  // holds it. The server picks the row from linkage; what the client must not
  // route around is the retirement of any handshake still out on the old
  // number, which only that write performs. A profile write behind a live
  // invitation leaves the old number in the invitation, and accepting on that
  // handset brings it back onto the account the acceptance mints.
  //
  // Clearing is a different act with no path there — the write takes a number.
  // For an unenrolled member an emptied box is a profile write instead; once
  // enrolled it is unreachable, because `isCardValid` requires the number.
  const phoneChanged = hasPhoneChanged(state, dep);
  const nextPhone = state.phone.trim();
  if (phoneChanged && nextPhone) {
    inputs.phone = { dependentId: dep.id, phoneNumber: nextPhone };
  } else if (phoneChanged && !dep.isAccountBacked) {
    inputs.contact = { dependantId: dep.id, phoneNumber: null };
  }

  // A changed email goes through `updateDependentEmail` whichever row holds it,
  // kept apart from the profile write above so a refusal names one field. An
  // emptied box is sent as a clear, which the server refuses once someone
  // enrolls — `isCardValid` holds that case back first. Compared
  // case-sensitively, so a case-only fix still reaches the row.
  if (state.email.trim() !== baseline.email.trim()) {
    inputs.email = { dependentId: dep.id, email: state.email.trim() || null };
  }

  // Residence upsert only in Main mode, and only when the built input actually
  // differs from the hydrated baseline — an untouched hydrated address re-builds
  // to the same row and must not mark the card dirty or re-fire a mutation.
  if (mode === 'main') {
    const [next] = buildResidenceInputs({ pathAForm: { dependentId: dep.id, form: state.residence } });
    const [base] = buildResidenceInputs({ pathAForm: { dependentId: dep.id, form: baseline.residence } });
    if (next && JSON.stringify(next) !== JSON.stringify(base ?? null)) {
      inputs.residence = next;
    }
  }

  return inputs;
};

/**
 * A card is savable when its required identity + relationship fields are valid
 * and the optional email + phone are blank or well-formed. The residence is only
 * validated when an actual upsert would be produced — Main mode, not living with
 * the caregiver, with an address picked — so a name-only edit on a dependent
 * with no address never blocks Save (a softer gate than onboarding's). The one
 * null-address case that does block: a persisted primary whose text was typed
 * over without picking a replacement (see `isResidenceSavable`).
 *
 * `isAccountBacked` requires both contact fields, because the account is reached
 * on them and neither write takes one away. For the phone it is also what keeps
 * an emptied box from diverging from the row it shows: clearing the field builds
 * no input — the mutation takes a number — so without the gate the save would
 * succeed, adopt the blank as the new baseline, and leave the card displaying no
 * number for an account that still has one.
 */
export const isCardValid = (
  mode: ResidenceMode,
  state: IDependentCardState,
  isValidDob: (dob: IDateOfBirthValue) => boolean,
  isAccountBacked = false
): boolean =>
  state.firstName.trim() !== '' &&
  state.lastName.trim() !== '' &&
  state.relationship !== null &&
  (state.dateOfBirth === null || isValidDob(state.dateOfBirth)) &&
  emailFieldError(state.email, isAccountBacked) === undefined &&
  validatePhoneNumber(state.phone, isAccountBacked, true).isValid &&
  isResidenceSavable(mode, state.residence);

const isResidenceSavable = (mode: ResidenceMode, residence: IResidenceFormState): boolean => {
  // Referential / lives-with-caregiver → no upsert is produced, nothing to validate.
  if (mode !== 'main' || residence.liveWithCaregiver !== false) {
    return true;
  }
  if (residence.address === null) {
    // Editing a saved address nulls the structured pick. Saving in that state
    // would drop the persisted primary from the keep-set and silently delete the
    // home — block until the user picks a replacement or clears the text (an
    // explicit clear is an intentional delete via the orphan diff).
    return !(residence.primaryResidenceId !== null && residence.addressText.trim() !== '');
  }
  return (
    isCompleteAddress(residence.address) &&
    residence.address.addressLine1 != null &&
    isResidenceCascadeComplete(residence)
  );
};

/** Live errors for the linked living-situation fields — see {@link residenceFieldErrors}. */
export interface IResidenceFieldErrors {
  address?: string;
  residenceType?: string;
  residenceTypeOther?: string;
  homeOwnership?: string;
  homeOwnershipOther?: string;
}

/**
 * Live errors for the living-situation unit. The address and the residence-type
 * cascade persist as ONE `upsertResidence` — none of it saves until every
 * engaged piece is filled — so each missing piece names what it's holding up.
 * An untouched unit (no address, no type) shows nothing: a dependent without a
 * residence is a valid state.
 */
export const residenceFieldErrors = (mode: ResidenceMode, residence: IResidenceFormState): IResidenceFieldErrors => {
  // Referential / lives-with-caregiver cards don't render the editor.
  if (mode !== 'main' || residence.liveWithCaregiver !== false) {
    return {};
  }
  const errors: IResidenceFieldErrors = {};
  const addressPicked = residence.address !== null;

  if (!addressPicked && residence.addressText.trim() !== '') {
    errors.address = 'Select an address from the suggestions to save it';
  } else if (!addressPicked && residence.residenceType !== null) {
    errors.address = 'Add an address to save this living situation';
  }
  if (addressPicked && residence.residenceType === null) {
    errors.residenceType = 'Select a living situation to save this address';
  }
  if (residence.residenceType === ResidenceType.Other && residence.residenceTypeOther.trim() === '') {
    errors.residenceTypeOther = 'Add a description to save this living situation';
  }
  if (residence.residenceType === ResidenceType.IndependentLiving) {
    if (residence.homeOwnership === null) {
      errors.homeOwnership = 'Select home ownership to save this living situation';
    } else if (residence.homeOwnership === HousingOwnership.Other && residence.homeOwnershipOther.trim() === '') {
      errors.homeOwnershipOther = 'Add a description to save this ownership';
    }
  }
  return errors;
};

/**
 * True for a saved frequent-location row whose structured pick was typed over
 * but not yet replaced. Such a row builds no upsert, so the orphan-delete diff
 * would sweep it — callers must keep its id until the user picks a replacement
 * or clears the text (an explicit clear is an intentional delete).
 */
export const isMidEditFrequentPlace = (place: IFrequentPlace): boolean =>
  place.id !== null && place.address === null && place.text.trim() !== '';

/**
 * The frequent-location rows a given card renders: every saved row (the
 * household-shared list) plus this card's own unsaved drafts. Another card's
 * drafts stay hidden here until a save persists them into the shared list.
 */
export const visibleFrequentPlaces = (places: readonly IFrequentPlace[], dependantId: string): IFrequentPlace[] =>
  places.filter((p) => p.id !== null || p.draftFor === undefined || p.draftFor === dependantId);

/**
 * Merge one card's edited visible rows back into the full household list,
 * preserving the other cards' unsaved drafts the card never saw.
 */
export const mergeFrequentPlaces = (
  places: readonly IFrequentPlace[],
  dependantId: string,
  nextVisible: readonly IFrequentPlace[]
): IFrequentPlace[] => [
  ...nextVisible,
  ...places.filter((p) => p.id === null && p.draftFor !== undefined && p.draftFor !== dependantId),
];
